"use client"

import styled from "@emotion/styled"
import { GetPageByRouteQuery } from "@eurodrive/apollo"
import { extractProperty, getNonUmbracoUrl, Link } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import {
  formAbandoned,
  formStarted,
  formSubmitted,
  phoneClick,
  whatsappClick,
} from "@eurodrive/utils/gtm"
import {
  getReCaptchaToken,
  loadReCaptcha,
} from "@eurodrive/utils/recaptcha"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, IconButton, Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { postContactUsLead } from "api"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useInView } from "react-intersection-observer"
import * as yup from "yup"
import Container from "./Container"
import {
  CustomCheckbox,
  CustomSelect,
  CustomTextField,
  MultilineCustomTextField,
} from "./form"
import { commonSchema } from "./FormSchema"
import ReCaptchaDisclaimer from "./ReCaptchaDisclaimer"
import Result from "./Result"
import { TransitionUp } from "./transitions/TransitionUp"
import { useInViewConfig } from "./transitions/utils"

const SOCIAL_NETWORK_LABELS: { match: RegExp; label: string }[] = [
  { match: /facebook\.com|fb\.com|fb\.me/i, label: "פייסבוק" },
  { match: /instagram\.com|instagr\.am/i, label: "אינסטגרם" },
  { match: /tiktok\.com/i, label: "טיקטוק" },
  { match: /youtube\.com|youtu\.be/i, label: "יוטיוב" },
  { match: /linkedin\.com/i, label: "לינקדאין" },
  { match: /twitter\.com|x\.com/i, label: "טוויטר" },
  { match: /t\.me|telegram/i, label: "טלגרם" },
]

const getSocialNetworkAriaLabel = (url?: string) => {
  if (!url) return "עבור לרשת החברתית"
  const network = SOCIAL_NETWORK_LABELS.find((n) => n.match.test(url))
  return network ? `עבור לדף ה${network.label}` : "עבור לרשת החברתית"
}

// minmax(0, 1fr) rather than 1fr: a plain fr track has an automatic minimum of
// min-content, and the subject select's placeholder is a long single line with
// white-space: nowrap, so its min-content (~493px) blew the content track past
// the viewport and made the whole page scroll sideways on mobile
const StyledLayout = styled(Box)`
  width: 100%;
  padding: 64px 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  /* md is 1024px here, and the image column only has room to be a fixed 515px
   * once the container is wide enough. Holding it fixed from md left the form
   * column at 209px and both name fields side by side in 97px each, so the two
   * columns share the space proportionally until lg. */
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 40px;
  }
  ${({ theme }) => theme.breakpoints.up("lg")} {
    grid-template-columns: minmax(0, 1fr) 515px;
    column-gap: 60px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    grid-template-columns: minmax(0, 1fr) 755px;
    column-gap: 100px;
  }
`

const StyledImage = styled.div`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    height: 757px;
    position: relative;
    width: 100%;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    height: 900px;
  }
  /* next/image fill defaults to object-fit: fill, which stretched the photo
   * to the full 757px of this box. At the narrower column widths the box is
   * tall and thin, so the car came out visibly distorted. */
  img {
    object-fit: cover;
  }
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const HeaderBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MainBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    justify-content: flex-start;
    gap: 30px;
  }
`

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    flex-direction: column;
  }
`

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledAnchor = styled.a`
  text-decoration: none;
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const InnerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
`

const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const InputRow = styled.div`
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
  }
  display: flex;
  gap: 16px;
  width: 100%;
  flex-direction: column;
`

const InputRowNames = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`

const schema = yup.object({
  firstName: commonSchema.common("he").firstName,
  lastName: commonSchema.common("he").lastName,
  email: commonSchema.common("he").email.optional(),
  phone: commonSchema.common("he").phone,
  subject: commonSchema.common("he").subject,
  // notRequired, not optional: yup's optional() drops the undefined check but
  // leaves the required test in place, so an empty string still fails. React
  // Hook Form hands back "" rather than undefined once the field has been typed
  // in, so a user who typed here and then cleared it could not submit — on a
  // field carrying neither an asterisk nor, since END-266, an optional hint.
  // The 250 character limit still applies.
  content: commonSchema.common("he").inquiryDescription.notRequired(),
  confirmReceipt: commonSchema.common("he").confirmReceipt,
  termsAgreement: commonSchema.common("he").termsAgreement,
})
type FormData = yup.InferType<typeof schema>

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 249px;
  }
`

// Mirrors the 250 character limit the remarks field is validated against.
const MESSAGE_MAX_LENGTH = 250

// Vehicle-page lead form: subject/ESSENSE mapping per lead context.
// Leasing pre-selects "ליסינג" and sends ESSENSE 3; Sale shows a trade-in
// dropdown that sends ESSENSE 2 (no trade-in) or 4 (with trade-in).
const LEASING_SUBJECT_LABEL = "ליסינג"
const LEASING_ESSENCE = "3"
const SALE_SUBJECT_OPTIONS: {
  value: string
  label: string
  essence: string
}[] = [
  {
    value: "רכישת רכב ללא טרייד אין",
    label: "רכישת רכב ללא טרייד אין",
    essence: "2",
  },
  {
    value: "רכישת רכב עם טרייד אין",
    label: "רכישת רכב עם טרייד אין",
    essence: "4",
  },
]

export default function FormBlock({
  contactForm,
  carLeadType,
  modelName,
}: {
  contactForm: GetPageByRouteQuery
  carLeadType?: "sale" | "leasing"
  modelName?: string
}) {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">(
    "idle",
  )
  const [formStartedTracked, setFormStartedTracked] = useState(false)
  const { ref: inViewRef, inView } = useInView(useInViewConfig)
  const data = extractProperty(contactForm.page, "contactForm")?.[0]
  const title = extractProperty(data, "title") as string
  const text = extractProperty(data, "text") as string
  const phoneIconUrl = extractProperty(data, "phoneIcon")
  const phoneNumber = extractProperty(data, "phoneNumber")?.[0]?.name
  const phoneUrl = extractProperty(data, "phoneNumber")?.[0]?.url
  const phoneTarget = extractProperty(data, "phoneNumber")?.[0]?.target
  const mailIconUrl = extractProperty(data, "mailIcon")
  const mailName = extractProperty(data, "mailLink")?.[0]?.name
  const mailUrl = extractProperty(data, "mailLink")?.[0]?.url
  const buttonName = extractProperty(data, "button")?.[0]?.name
  const buttonUrl = extractProperty(data, "button")?.[0]?.url
  const buttonTarget = extractProperty(data, "button")?.[0]?.target
  const socialText = extractProperty(data, "socialText") as string
  const imageUrl = extractProperty(data, "image")
  const formSubectsObject = extractProperty(data, "formSubjects")?.map(
    (item: any) => {
      const subject = extractProperty(item, "subject")
      const formNumber = extractProperty(item, "formNumber")

      return {
        value: subject,
        label: subject,
        formNumber: formNumber,
      }
    },
  )
  const socialItems =
    extractProperty(data, "itemsList")?.map((item: any) => ({
      iconUrl: extractProperty(item, "icon"),
      linkUrl: extractProperty(item, "link")?.[0]?.url,
      linkTarget: extractProperty(item, "link")?.[0]?.target,
    })) || []
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isSubmitted, isValid },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues:
      carLeadType === "leasing"
        ? { subject: LEASING_SUBJECT_LABEL }
        : carLeadType === "sale"
          ? { subject: "" } // controlled empty -> shows placeholder, forces an active choice
          : undefined,
  })

  // Preload reCAPTCHA so submit is instant (the badge itself is hidden)
  useEffect(() => {
    loadReCaptcha()
  }, [])

  // Track form abandonment on page unload if form was started but not submitted
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formStartedTracked && formStatus === "idle") {
        const formValues = watch()
        const filledFields = Object.entries(formValues)
          .filter(([_, value]) => value && String(value).length > 0)
          .map(([key]) => key)
          .join(",")

        if (filledFields) {
          formAbandoned("contact_form", filledFields, "page_exit")
        }
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [formStartedTracked, formStatus, watch])

  const mutation = useMutation({
    mutationFn: postContactUsLead,
    onSuccess: (response) => {
      // API returns HTTP 200 even for validation errors — check errorCode/errorMessage
      const { errorCode, errorMessage } = response.data || {}
      if (errorCode !== 0 || errorMessage) {
        console.error("API validation error:", errorMessage)
        setFormStatus("error")
        return
      }
      setFormStatus("success")
      // Track form submission
      formSubmitted("contact_form", Object.keys(errors).join(","))
      // Reset form on success
      reset()
    },
    onError: (error) => {
      console.error("Failed to submit contact form:", error)
      setFormStatus("error")
    },
  })

  const onSubmit = async (data: FormData) => {
    // Resolve ESSENSE per lead context. Vehicle-page leads send a numeric code
    // (leasing = 3, sale = 2/4 by trade-in choice); the generic contact form
    // keeps the Umbraco formNumber lookup.
    let essence: string
    if (carLeadType === "leasing") {
      essence = LEASING_ESSENCE
    } else if (carLeadType === "sale") {
      const saleOption = SALE_SUBJECT_OPTIONS.find(
        (option) => option.value === data.subject,
      )
      essence = saleOption?.essence || data.subject
    } else {
      const selectedOption = formSubectsObject?.find(
        (option: any) => option.value === data.subject,
      )
      essence = selectedOption?.formNumber || data.subject
    }

    // Append the current vehicle model name to remarks before submission. The
    // remarks field is capped at 250 characters, so trim the user's own text
    // rather than the model name if the two together would overflow it.
    let message = data.content || ""
    if (carLeadType && modelName) {
      const suffix = message ? ` | ${modelName}` : modelName
      const room = MESSAGE_MAX_LENGTH - suffix.length
      message =
        room > 0
          ? `${message.slice(0, room)}${suffix}`
          : modelName.slice(0, MESSAGE_MAX_LENGTH)
    }

    // Fresh token per submit; server verifies it (action must match exactly)
    const reCaptchaToken = await getReCaptchaToken("contact_us_lead")

    // Map form data to API interface
    const apiData = {
      firstName: data.firstName,
      lastName: data.lastName,
      emailAddress: data.email,
      phoneNumber: data.phone,
      essence,
      agreedNewsLetter: data.confirmReceipt || false,
      message,
      reCaptchaToken,
    }

    mutation.mutate(apiData as any)
  }

  const handleRetry = () => {
    setFormStatus("idle")
  }

  // Track when form is started (first input focus)
  const handleFormStarted = () => {
    if (!formStartedTracked) {
      formStarted("contact_form", "contact_us_page")
      setFormStartedTracked(true)
    }
  }

  // Track phone click
  const handlePhoneClick = () => {
    phoneClick(phoneNumber || "", "contact_form")
  }

  // Track WhatsApp click
  const handleWhatsAppClick = (linkUrl: string) => {
    whatsappClick(linkUrl, "contact_form_social")
  }

  // Show result component if form was submitted
  if (formStatus === "success" || formStatus === "error") {
    return (
      <Container paddingMobile>
        <StyledLayout ref={inViewRef} role="alert">
          <ContentLayout>
            <TransitionUp $inView={inView}>
              <Result
                type={formStatus}
                onRetry={formStatus === "error" ? handleRetry : undefined}
              />
            </TransitionUp>
          </ContentLayout>
          <StyledImage>
            <Image
              src={getUmbracoMediaUrl(imageUrl || "")}
              alt=""
              aria-hidden="true"
              fill
              sizes="(min-width: 1900px) 755px, (min-width: 1280px) 515px, 50vw"
            />
          </StyledImage>
        </StyledLayout>
      </Container>
    )
  }
  return (
    <Container paddingMobile>
      <StyledLayout ref={inViewRef}>
        <ContentLayout>
          <TransitionUp $inView={inView}>
            <HeaderBox>
              <Typography variant="h2">{title}</Typography>
              <Typography variant="body2">{text}</Typography>
            </HeaderBox>

            <MainBox>
              <ContactRow>
                <ContactItem>
                  <Image
                    src={getUmbracoMediaUrl(phoneIconUrl || "")}
                    alt=""
                    aria-hidden="true"
                    width={24}
                    height={24}
                  />
                  <StyledLink
                    href={phoneUrl || ""}
                    target={phoneTarget || ""}
                    onClick={handlePhoneClick}
                    aria-label={`מספר טלפון לשירות לקוחות: ${phoneNumber}`}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {phoneNumber}
                    </Typography>
                  </StyledLink>
                </ContactItem>
                <ContactItem>
                  <Image
                    src={getUmbracoMediaUrl(mailIconUrl || "")}
                    alt=""
                    aria-hidden="true"
                    width={24}
                    height={24}
                  />
                  <StyledAnchor
                    href={
                      mailUrl?.startsWith("mailto:")
                        ? mailUrl
                        : `mailto:${mailUrl || mailName || ""}`
                    }
                    aria-label={`כתובת אימייל: ${mailName}`}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {mailName}
                    </Typography>
                  </StyledAnchor>
                </ContactItem>
              </ContactRow>
              <InnerContent>
                <StyledForm
                  onSubmit={handleSubmit(onSubmit)}
                  aria-label="טופס יצירת קשר - מלא את הפרטים שלך ונחזור אליך בהקדם"
                >
                  <Typography variant="body3" component="p">
                    שדות המסומנים ב-<span aria-label="כוכבית">*</span> הם שדות
                    חובה
                  </Typography>
                  <InputRowNames>
                    <CustomTextField
                      name="firstName"
                      control={control}
                      label="שם פרטי*"
                      placeholder="לדוגמא לוי"
                      required
                      errors={errors}
                      isSubmitted={isSubmitted}
                      slotProps={{
                        htmlInput: {
                          autoComplete: "given-name",
                          onFocus: handleFormStarted,
                        },
                      }}
                    />
                    <CustomTextField
                      name="lastName"
                      control={control}
                      label="שם משפחה*"
                      placeholder="לדוגמא לוי"
                      required
                      errors={errors}
                      isSubmitted={isSubmitted}
                      slotProps={{
                        htmlInput: {
                          autoComplete: "family-name",
                        },
                      }}
                    />
                  </InputRowNames>
                  <InputRow>
                    <CustomTextField
                      name="email"
                      control={control}
                      errors={errors}
                      label="דוא״ל"
                      placeholder="example@gmail.com"
                      isSubmitted={isSubmitted}
                      slotProps={{
                        htmlInput: { autoComplete: "email" },
                      }}
                    />
                    <CustomTextField
                      name="phone"
                      errors={errors}
                      control={control}
                      label="טלפון*"
                      placeholder="לדוגמה: 058-123-4567"
                      required
                      isSubmitted={isSubmitted}
                      slotProps={{
                        htmlInput: {
                          autoComplete: "tel-national",
                        },
                      }}
                    />
                  </InputRow>
                  {carLeadType === "sale" ? (
                    <CustomSelect
                      isSubmitted={isSubmitted}
                      control={control}
                      name="subject"
                      label="באיזה נושא נוכל לעזור?*"
                      required
                      errors={errors}
                      placeholder="בחירת סוג רכישה"
                      options={SALE_SUBJECT_OPTIONS}
                    />
                  ) : carLeadType === "leasing" ? (
                    <CustomTextField
                      name="subject"
                      control={control}
                      label="באיזה נושא נוכל לעזור?*"
                      required
                      errors={errors}
                      isSubmitted={isSubmitted}
                      slotProps={{
                        input: {
                          readOnly: true,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor: "action.disabledBackground",
                        },
                      }}
                    />
                  ) : (
                    <CustomSelect
                      isSubmitted={isSubmitted}
                      control={control}
                      name="subject"
                      label="באיזה נושא נוכל לעזור?*"
                      required
                      errors={errors}
                      placeholder="בחרו נושא מתוך הרשימה (ליסינג פרטי, ליסינג תפעולי, קנייה/טרייד-אין, מימון)"
                      options={formSubectsObject || []}
                    />
                  )}
                  <MultilineCustomTextField
                    name="content"
                    control={control}
                    errors={errors}
                    label="פירוט או בקשה מיוחדת"
                    placeholder="ספרו לנו באיזה דגם אתם מתעניינים או איזה מסלול מעניין אתכם..."
                    isSubmitted={isSubmitted}
                  />
                  <CheckboxRow>
                    <CustomCheckbox
                      name="termsAgreement"
                      control={control}
                      label="אני מאשר/ת את תנאי השימוש"
                      required
                      isSubmitted={isSubmitted}
                      errors={errors}
                    />
                    <CustomCheckbox
                      name="confirmReceipt"
                      control={control}
                      label="אני מאשר/ת את קבלת הדיוור"
                      isSubmitted={isSubmitted}
                      errors={errors}
                    />
                  </CheckboxRow>
                  <StyledButton
                    variant="primary"
                    size="large"
                    type="submit"
                    disabled={mutation.isPending}
                  >
                    {buttonName}
                  </StyledButton>
                </StyledForm>
                <ContactRow>
                  <Typography variant="body2">{socialText}</Typography>
                  <SocialIcons>
                    {socialItems.map((el: any, i: number) => {
                      const isWhatsApp =
                        el.linkUrl?.includes("whatsapp") ||
                        el.linkUrl?.includes("wa.me")
                      return (
                        <IconButton
                          key={i}
                          LinkComponent={Link}
                          href={getNonUmbracoUrl(el.linkUrl || "")}
                          target={el.linkTarget || ""}
                          aria-label={
                            isWhatsApp
                              ? "צור קשר בוואטסאפ"
                              : getSocialNetworkAriaLabel(el.linkUrl)
                          }
                          onClick={
                            isWhatsApp
                              ? () => handleWhatsAppClick(el.linkUrl || "")
                              : undefined
                          }
                        >
                          <Image
                            src={getUmbracoMediaUrl(el.iconUrl || "")}
                            alt=""
                            aria-hidden="true"
                            width={24}
                            height={24}
                          />
                        </IconButton>
                      )
                    })}
                  </SocialIcons>
                </ContactRow>
                <ReCaptchaDisclaimer />
              </InnerContent>
            </MainBox>
          </TransitionUp>
        </ContentLayout>
        <StyledImage>
          <Image
            src={getUmbracoMediaUrl(imageUrl || "")}
            alt={""}
            fill
            sizes="(min-width: 1900px) 755px, (min-width: 1280px) 515px, 50vw"
          />
        </StyledImage>
      </StyledLayout>
    </Container>
  )
}
